import React, {
  useEffect,
  useState,
} from "react";

import {
  Stack,
  Input,
  FormLabel,
  Checkbox,
  Image,
  Box,
} from "@chakra-ui/react";

import ReusableDrawer from "components/Drawers/ReusableDrawer";
import ReusableInput from "components/Inputs/ReusableInput";
import GradientButton from "components/Buttons/GradientButton";


const EditStarterKitComponentDrawer = ({
  isOpen,
  onClose,
  starterKitComponent,
  onUpdateStarterKitComponent,
}) => {

  const [
    formData,
    setFormData,
  ] = useState({
    component_name: "",
    description: "",
    unit_price: "",
    quantity_in_stock: "",
    minimum_stock_level: "",
    is_mandatory: true,
    active: true,
    display_order: "",
  });


  const [
    isEditing,
    setIsEditing,
  ] = useState(false);


  const BASE_URL =
    process.env.REACT_APP_API_BASE_URL;


  const buildImageUrl = (
    baseUrl,
    path
  ) => {

    if (!baseUrl || !path) {
      return null;
    }

    const cleanBase =
      baseUrl.replace(/\/$/, "");

    const cleanPath =
      String(path).replace(
        /^\/+/,
        ""
      );

    return `${cleanBase}/${cleanPath}`;
  };


  useEffect(() => {

    if (starterKitComponent) {

      setFormData({
        component_name:
          starterKitComponent.component_name ||
          "",

        description:
          starterKitComponent.description ||
          "",

        unit_price:
          starterKitComponent.unit_price ??
          "",

        quantity_in_stock:
          starterKitComponent.quantity_in_stock ??
          "",

        minimum_stock_level:
          starterKitComponent.minimum_stock_level ??
          "",

        is_mandatory:
          starterKitComponent.is_mandatory ??
          true,

        active:
          starterKitComponent.active ??
          true,

        display_order:
          starterKitComponent.display_order ??
          "",
      });

      setIsEditing(false);
    }

  }, [starterKitComponent]);


  const handleInputChange = (e) => {

    const {
      name,
      value,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const handleCheckboxChange = (e) => {

    const {
      name,
      checked,
    } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };


  const handleToggleEdit = async () => {

    if (isEditing) {

      const payload = {
        component_name:
          formData.component_name,

        description:
          formData.description,

        unit_price:
          Number(formData.unit_price),

        quantity_in_stock:
          Number(
            formData.quantity_in_stock
          ),

        minimum_stock_level:
          Number(
            formData.minimum_stock_level
          ),

        is_mandatory:
          formData.is_mandatory,

        active:
          formData.active,

        display_order:
          Number(
            formData.display_order
          ),
      };


      const response =
        await onUpdateStarterKitComponent(
          starterKitComponent.starter_kit_component_id,
          payload
        );


      if (
        response?.success &&
        response?.data
      ) {

        setFormData({
          component_name:
            response.data.component_name ||
            "",

          description:
            response.data.description ||
            "",

          unit_price:
            response.data.unit_price ??
            "",

          quantity_in_stock:
            response.data.quantity_in_stock ??
            "",

          minimum_stock_level:
            response.data.minimum_stock_level ??
            "",

          is_mandatory:
            response.data.is_mandatory ??
            true,

          active:
            response.data.active ??
            true,

          display_order:
            response.data.display_order ??
            "",
        });
      }


      setIsEditing(false);

      onClose();

      return;
    }


    setIsEditing(true);
  };


  return (
    <ReusableDrawer
      isOpen={isOpen}

      onClose={() => {
        setIsEditing(false);
        onClose();
      }}

      headerContent={
        <h2>
          {isEditing
            ? "Edit Starter Kit Component"
            : "View Starter Kit Component"}
        </h2>
      }

      bodyContent={
        <Stack spacing={4}>

          <ReusableInput
            label="Component Code"
            value={
              starterKitComponent?.component_code ||
              ""
            }
            isReadOnly
          />


          <ReusableInput
            label="Component Name"
            name="component_name"
            value={
              formData.component_name
            }
            onChange={
              handleInputChange
            }
            placeholder="Enter component name"
            isReadOnly={!isEditing}
          />


          <ReusableInput
            label="Description"
            name="description"
            value={
              formData.description
            }
            onChange={
              handleInputChange
            }
            placeholder="Enter description"
            type="textarea"
            isReadOnly={!isEditing}
          />


          <ReusableInput
            label="Unit Price"
            name="unit_price"
            type="number"
            value={
              formData.unit_price
            }
            onChange={
              handleInputChange
            }
            isReadOnly={!isEditing}
          />


          <ReusableInput
            label="Quantity in Stock"
            name="quantity_in_stock"
            type="number"
            value={
              formData.quantity_in_stock
            }
            onChange={
              handleInputChange
            }
            isReadOnly={!isEditing}
          />


          <ReusableInput
            label="Minimum Stock Level"
            name="minimum_stock_level"
            type="number"
            value={
              formData.minimum_stock_level
            }
            onChange={
              handleInputChange
            }
            isReadOnly={!isEditing}
          />


          <ReusableInput
            label="Display Order"
            name="display_order"
            type="number"
            value={
              formData.display_order
            }
            onChange={
              handleInputChange
            }
            isReadOnly={!isEditing}
          />


          <Checkbox
            name="is_mandatory"
            isChecked={
              formData.is_mandatory
            }
            onChange={
              handleCheckboxChange
            }
            isDisabled={!isEditing}
          >
            Mandatory component
          </Checkbox>


          <Checkbox
            name="active"
            isChecked={
              formData.active
            }
            onChange={
              handleCheckboxChange
            }
            isDisabled={!isEditing}
          >
            Active
          </Checkbox>


          {starterKitComponent?.component_images
            ?.length > 0 && (

            <Box>

              <FormLabel>
                Component Images
              </FormLabel>

              {starterKitComponent.component_images.map(
                (image, index) => {

                  const imageUrl =
                    buildImageUrl(
                      BASE_URL,
                      image
                    );

                  return (
                    <Image
                      key={index}
                      src={imageUrl}
                      alt={
                        starterKitComponent.component_name
                      }
                      maxH="180px"
                      objectFit="contain"
                      mb={3}
                    />
                  );
                }
              )}

            </Box>
          )}

        </Stack>
      }

      footerContent={
        <Stack
          direction="row"
          spacing={4}
          justifyContent="flex-end"
        >

          <GradientButton
            fontSize="15px"
            width="100px"
            height="40px"
            onClick={() => {
              setIsEditing(false);
              onClose();
            }}
          >
            Cancel
          </GradientButton>


          <GradientButton
            fontSize="15px"
            width="100px"
            height="40px"
            onClick={
              handleToggleEdit
            }
            isDisabled={
              isEditing &&
              !formData.component_name
            }
          >
            {isEditing
              ? "Save"
              : "Edit"}
          </GradientButton>

        </Stack>
      }
    />
  );
};


export default EditStarterKitComponentDrawer;